import React, {useState} from 'react';
import {
  Button,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {Container} from '@mui/system';
import Iconify from 'src/minimal-components/iconify';
import InfoIcon from '@mui/icons-material/Info';

interface GradeCategory {
  id: number;
  name: string;
  weightage: number;
}

const GradingSystem: React.FC = () => {
  const [gradeCategories, setGradeCategories] = useState<GradeCategory[]>([{id: Date.now(), name: '', weightage: 0}]);
  const [totalWeightage, setTotalWeightage] = useState<number>(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const [gradeType, setGradeType] = useState<string>('');
  const [openmarks, setOpenmarks] = useState<boolean>(false);

  const handleAddCategory = () => {
    const newCategory: GradeCategory = {id: Date.now(), name: '', weightage: 0};
    setGradeCategories([...gradeCategories, newCategory]);
  };

  const handleNameChange = (id: number, newName: string) => {
    setGradeCategories(
      gradeCategories.map(category =>
        category.id === id ? {...category, name: newName} : category
      )
    );
  };

  const handleWeightageChange = (id: number, newWeightage: string) => {
    // Allow deletion to empty string and restrict total weightage to 100%
    const weightageValue = newWeightage === '' ? 0 : parseInt(newWeightage, 10);
    const newCategories = gradeCategories.map(category => {
      if (category.id === id) {
        return {
          ...category,
          weightage: newWeightage === '' ? 0 : parseInt(newWeightage, 10)
        };
      }
      return category;
    });

    // Compute the total weightage without the current category's new value
    const totalExcludingCurrent = newCategories.reduce(
      (acc, category) => (category.id !== id ? acc + category.weightage : acc),
      0
    );

    // If the total weightage is not exceeding 100%, allow the change
    if (newWeightage === '' || weightageValue + totalExcludingCurrent <= 100) {
      setGradeCategories(newCategories);
    }

    // Update the total weightage display
    setTotalWeightage(
      newCategories.reduce((acc, category) => acc + category.weightage, 0)
    );
  };

  const confirmDeleteCategory = (id: number) => {
    setCategoryToDelete(id);
    setOpenDialog(true);
  };

  const deleteCategory = () => {
    if (categoryToDelete !== null) {
      const newCategories = gradeCategories.filter(
        category => category.id !== categoryToDelete
      );
      const total = newCategories.reduce(
        (acc, category) => acc + category.weightage,
        0
      );

      setGradeCategories(newCategories);
      setTotalWeightage(total);
      setOpenDialog(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleAttributeChange = (value: any) => {
    setGradeType(value);
    if (value === 'passFail') {
      setOpenmarks(true);
    }
  };
  const handleSave = () => {
    console.log(gradeCategories);
  };
  const handlepasspercentage = (e: any) => {
    console.log(e);
  };
  // ...

  return (
    <>
      <Container
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '100% !important',
          py: '3vh'
        }}>
        <Grid
          container
          spacing={2}
          sx={{margin: '0 !important', width: '100% !important'}}>
          <Grid item xs={12}>
            <Typography variant="h3" sx={{py: '2vh'}}>
              Select a grading system for this course
            </Typography>
            <FormControl sx={{width: '20%'}}>
              <InputLabel
                sx={{display: gradeType.length <= 0 ? 'flex' : 'none'}}>
                Select
              </InputLabel>
              <Select
                defaultValue=""
                onChange={e => handleAttributeChange(e.target.value)}>
                {/* Populate with your grading systems */}
                <MenuItem value="passFail">Pass/Fail</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {openmarks && (
            <Grid item xs={12}>
              <div style={{display: 'flex'}}>
                <Typography
                  variant="h3"
                  sx={{py: '3vh', whiteSpace: 'pre-wrap'}}>
                  Course pass percentage:{' '}
                </Typography>
                <TextField
                  type="number"
                  defaultValue={30}
                  margin="normal"
                  sx={{width: '5vh', whiteSpace: 'pre-wrap'}}
                  inputProps={{min: 0, max: 100}}
                  onChange={e => handlepasspercentage(e)}
                />{' '}
                <Typography
                  variant="h3"
                  sx={{py: '3vh', whiteSpace: 'pre-wrap'}}>
                  {'  '} %
                </Typography>
              </div>
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{
                  borderBottom: '1px solid #edeff3',
                  pb: '1vh',
                  justifyContent: 'space-between'
                }}>
                <Grid item xs={3}>
                  <Typography variant="h4">Grade Category</Typography>
                </Grid>
                <Grid item xs={2} sx={{paddingRight: '1vh'}}>
                  <Typography variant="h4">Weightage Percentage</Typography>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{paddingTop: '2vh'}}>
                {gradeCategories.map((category, index) => (
                  <React.Fragment key={category.id}>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        justifyContent: 'space-between',
                        pb: '2vh',
                        display: 'flex'
                      }}>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Grade Category"
                          value={category.name}
                          onChange={e =>
                            handleNameChange(category.id, e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <div style={{display: 'flex'}}>
                          <TextField
                            fullWidth
                            type="text" // Change type to text to allow an empty string
                            value={category.weightage.toString()} // Convert the number to a string for the TextField
                            onChange={e =>
                              handleWeightageChange(category.id, e.target.value)
                            }
                            inputProps={{min: 0, max: 100, pattern: '\\d*'}} // pattern to restrict input to digits
                          />
                          <IconButton
                            onClick={() => confirmDeleteCategory(category.id)}
                            sx={{color: 'error.main'}}>
                            <Iconify icon="solar:trash-bin-trash-bold" />
                          </IconButton>
                        </div>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={12} py={'2vh'}>
                  <Button variant="contained" onClick={handleAddCategory}>
                    + Add category
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{display: 'flex', justifyContent: 'space-between'}}>
                  <Grid item xs={2} sx={{backgroundColor: '#dce796',display:'flex'}}>
                    <InfoIcon
                      style={{width: '0.7em', height: '0.7em'}}
                      color="action"
                    />
                    <Typography variant="h6" sx={{display: 'flex'}}>
                      Cannot exceed 100%
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sx={{paddingLeft: '1vh', borderTop: '1px solid #edeff3'}}>
                    <Typography
                      variant="h3"
                      color={totalWeightage > 100 ? 'error' : 'inherit'}>
                      {`Total: ${totalWeightage}%`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{marginRight: '2vh'}}>
              Save
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                /* Cancel logic here */
              }}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this grade category?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={deleteCategory} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GradingSystem;
